import { ParsedUrlQuery } from 'querystring';

import { useEffect, useMemo, useState } from 'react';

import { useIsFetching } from '@tanstack/react-query';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { FieldErrors } from 'react-hook-form';
import dynamic from 'next/dynamic';

import { SearchViewModel } from '@api/v3';
import {
  LocationAnimationErrorBox,
  SearchButton,
} from '@components/views/components/listing/ListingStyle';
import { TOUR_PAST_SEARCH } from '@constants/constants';
import SearchMonthDate from '@components/ui/search/SearchMonthDate';
import { YearTabProps } from '@components/ui/search/SearchMonthDate/interface';
import {
  generateDateParam,
  getCurrentYearTab,
  getNextYearTab,
} from '@components/ui/search/SearchMonthDate/helpers';
import clean from '@utils/cleanObject';
import { useDevice } from '@contexts/device';
import { isServer } from '@utils/next';
import { SearchWrapper } from '@components/views/travel-planning/components/modals/styled';
import { productImpression } from '@utils/newDataLayer/productImpression';
import { paramsToObject } from '@components/views/utils';
import useRouterNext from '@hooks/useRouterNext';

import DestinationSearch from '../../../components/DestinationSearch/DestinationSearch';
import { SearchDateWrapper, SearchInputWrapper, Wrapper } from './style';

const MobileSearchBar = dynamic(() => import('./MobileSearchBar'));

interface SearchBarProps {
  tourListCount: number;
  type?: 'hotel' | 'tour' | 'ship';
  handleSearch?: () => void;
  detailQueryKey?: string;
  tourStyle?: boolean;
  tourListing?: boolean;
  searchText?: string;
  shouldShallowPush?: boolean;
  destinationSearchText?: string;
  destination?: SearchViewModel;
  onDestinationTextChange: (s: string) => void;
  onDestinationChange: (d: SearchViewModel) => void;
  errors: FieldErrors<{ destination: string }>;
  sticky?: boolean;
  searchType?: string;
  detail?: boolean;
  destinationError?: boolean;
  setIsRedirecting?: (isRedirecting: boolean) => void;
  isSegmentPage?: boolean;
}
const SearchBar = ({
  tourListCount,
  type = 'tour',
  handleSearch,
  detailQueryKey = 'tourDetail',
  tourStyle,
  tourListing,
  shouldShallowPush,
  destination,
  destinationSearchText,
  onDestinationChange,
  onDestinationTextChange,
  searchType,
  detail,
  destinationError,
  setIsRedirecting,
  isSegmentPage,
}: SearchBarProps) => {
  const params = useParams();
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);
  const router = useRouter();
  const { push } = useRouterNext();
  const isTourListingLoading = useIsFetching([`${type}-listing`]);
  const isTourDetailLoading = useIsFetching([detailQueryKey]);
  const { isDesktop, isMobile, isTablet } = useDevice();
  const [buttonDisable, setButtonDisable] = useState<boolean>(false);
  const [currentYearTab, setCurrentYearTab] = useState<YearTabProps>({
    year: getCurrentYearTab().year,
    months: [...getCurrentYearTab().months],
  });
  const [nextYearTab, setNextYearTab] = useState<YearTabProps>({
    year: getNextYearTab().year,
    months: [...getNextYearTab().months],
  });
  const [errors, setErrors] = useState({
    destination: false,
  });
  const onResearch = () => {
    if (type === 'tour')
      productImpression({
        item_list_location: 'Listing Page',
        item_list_id: destination?.text || '',
        type: 'tour',
      });
    if (type === 'ship')
      productImpression({
        item_list_location: 'Listing Page',
        item_list_id: destination?.text || '',
        type: 'ship',
      });
    setButtonDisable?.(false);
    if (!destination || !destination.url) {
      setErrors({ ...errors, destination: true });
      return;
    } else {
      setErrors({ ...errors, destination: false });
    }
    let pastTourItems = [];
    let tourItems = [];
    if (localStorage.getItem(TOUR_PAST_SEARCH)) {
      pastTourItems = JSON.parse(localStorage.getItem(TOUR_PAST_SEARCH) || '');
    }

    tourItems = pastTourItems ? [...pastTourItems] : [];

    if (!tourItems.find((i) => i.url === destination?.url)) {
      tourItems.unshift({
        id: Math.random(),
        text: destination?.text,
        url: destination?.url,
      });
    } else {
      const target = tourItems.find((i) => i.url === destination?.url);

      const targetIndex = tourItems.indexOf(target);

      tourItems.splice(targetIndex, 1);
      tourItems.unshift({
        id: Math.random(),
        text: destination?.text,
        url: destination?.url,
      });
    }
    localStorage.setItem(TOUR_PAST_SEARCH, JSON.stringify(tourItems));
    handleSearch?.();
    const currentPath = params?.slug as string;
    const targetUrl = destination?.url;
    setIsRedirecting?.(true);

    if (destination.redirectUrl) {
      router.push(`/${targetUrl}`);
    } else {
      const newQuery = clean({
        date: generateDateParam(currentYearTab, nextYearTab),
      });
      if (currentPath !== destination.url) {
        const queryString = new URLSearchParams(newQuery).toString();
        const href = `${targetUrl}?${queryString}`;
        router.push(href);
      } else {
        push({
          pathname: targetUrl,
          query: newQuery,
        });
      }
    }
  };
  const handleOnMonthsDateUpdate = (info: YearTabProps[]) => {
    const [currentYear, nextYear] = info;

    setCurrentYearTab(currentYear);
    setNextYearTab(nextYear);
  };
  useEffect(() => {
    if (destination?.url != params?.slug) {
      setButtonDisable?.(true);
    } else if (
      (query.date || String(generateDateParam(currentYearTab, nextYearTab))) &&
      String(generateDateParam(currentYearTab, nextYearTab)) != query.date
    ) {
      setButtonDisable?.(true);
    } else {
      setButtonDisable?.(false);
    }
  }, [setButtonDisable, destination, query, currentYearTab, nextYearTab]);

  useEffect(() => {
    if (destination?.url === params?.slug) {
      setIsRedirecting?.(false);
    }
  }, [destination, params]);

  if (isServer()) return null; // The search bar uses react-tiny-popover and its not working on the server side so will not render it on the server

  return isDesktop ? (
    <Wrapper>
      <SearchInputWrapper>
        <LocationAnimationErrorBox
          borderRadius='8px'
          hasError={destinationError}
        >
          <SearchWrapper searchType={searchType}>
            <DestinationSearch
              tourListing={tourListing}
              tourStyle={tourStyle}
              onDestinationChange={onDestinationChange}
              searchText={destinationSearchText}
              onSearchTextChange={onDestinationTextChange}
              type={type}
              borderRadius='8px'
            />
          </SearchWrapper>
        </LocationAnimationErrorBox>
      </SearchInputWrapper>
      <SearchDateWrapper>
        <SearchMonthDate onMonthsUpdated={handleOnMonthsDateUpdate} />
      </SearchDateWrapper>

      <SearchButton
        data-testid='listing-search-button'
        variant='Secondary'
        type='submit'
        disabled={
          buttonDisable
            ? false
            : true || !!isTourListingLoading || !!isTourDetailLoading
        }
        onClick={onResearch}
      >
        Yeniden Ara
      </SearchButton>
    </Wrapper>
  ) : isMobile || isTablet ? (
    <MobileSearchBar
      destination={destination}
      onDestinationChange={onDestinationChange}
      tourListCount={tourListCount}
      type={type}
      shouldShallowPush={shouldShallowPush}
      searchText={destinationSearchText}
      detail={detail}
      setIsRedirecting={setIsRedirecting}
      isSegmentPage={isSegmentPage}
    />
  ) : null;
};

export default SearchBar;
